import React, { createContext, useContext, useEffect, useReducer, useState } from 'react';
import { algoliaClient } from 'src/clients/Algolia';
import { history } from 'instantsearch.js/es/lib/routers';
import { InstantSearch, Configure } from 'react-instantsearch-hooks-web';
import { LoadingOverlay } from 'src/components/LoadingOverlay';
import { useMemberExperienceAuth } from '@guildeducationinc/auth-frontend-tools';
import { datadogRum } from '@datadog/browser-rum';
import {
	SearchReducerInterface,
	searchInitialState,
	searchReducer,
} from '@src/reducers/searchReducer';
import { useLocation } from 'react-router-dom';
import { GetMemberPlanQuery, useGetMemberPlanQuery } from '@src/graphqlTypes/catalogServiceTypes';
import { AlgoliaFacetsType } from '@src/clients/Algolia/constants';
import { useDecision } from '@optimizely/react-sdk';
import { ROLE_MAPPING } from '@src/constants/featureFlags';
import {
	HITS_PER_PAGE,
	onStateChange,
	updateNoProgramsSearchState,
	updateSearchState,
} from './helpers';
import { formatRouteToState, formatStateToRoute } from './routeHelpers';

export const SearchContext = createContext<SearchReducerInterface>(null);
export const SearchDispatchContext = createContext(null);

export const useSearchContext = () => {
	const context = useContext(SearchContext);

	if (context === undefined) {
		datadogRum.addError('Context error.', {
			catalogContextError: 'useSearch must be used within SearchProvider.',
			catalogContext: 'useSearchContext',
		});
		throw new Error('useSearch must be used within SearchProvider.');
	}

	return context;
};
export const useSearchDispatch = () => {
	const context = useContext(SearchDispatchContext);

	if (context === undefined) {
		datadogRum.addError('Context error', {
			catalogContextError: 'useSearchDispatch must be used within SearchProvider.',
			catalogContext: 'useSearchDispatch',
		});
		throw new Error('useSearchDispatch must be used within SearchProvider.');
	}

	return context;
};

export const SearchProvider: React.FC = ({ children }) => {
	const { userFromIdToken: user } = useMemberExperienceAuth();
	const [rolesDecision] = useDecision(ROLE_MAPPING);
	const [searchState, dispatch] = useReducer(searchReducer, searchInitialState);
	const location = useLocation();
	const isMemberServicesRoute = location.pathname.includes('memberservices');
	const memberServicesEmployerUuid = location.pathname.split('/')[2];
	const memberServicesMemberUuid = location.pathname.split('/')[3];
	const [employerUuid, setEmployerUuid] = useState(null);
	const [memberUuid, setMemberUuid] = useState(null);

	useEffect(() => {
		const employerId = isMemberServicesRoute ? memberServicesEmployerUuid : user?.employer_uuid;
		const memberId = isMemberServicesRoute ? memberServicesMemberUuid : user?.id;

		setEmployerUuid(employerId);
		setMemberUuid(memberId);

		dispatch({
			type: 'set_member_and_employer_uuids',
			memberUuid: memberId,
			employerUuid: employerId,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useGetMemberPlanQuery({
		variables: { memberUuid, employerUuid },
		skip: !memberUuid || !employerUuid,
		onCompleted: async (data: GetMemberPlanQuery) => {
			if (data.memberPlan && !data.memberPlan.hasPrograms) {
				dispatch({
					type: 'set_no_plan_programs_returned',
					facets: {} as AlgoliaFacetsType,
					indexAvailable: false,
				});
				// necessary for funded only program pages to render
				updateNoProgramsSearchState({
					dispatch,
					employerUuid,
					isMemberServicesRoute,
					memberUuid,
					planUuid: data.memberPlan.planUuid,
					response: data,
				});
				datadogRum.addAction('No plan programs returned', {
					memberUuid,
					employerUuid,
				});
			} else {
				await updateSearchState(
					rolesDecision,
					dispatch,
					data,
					isMemberServicesRoute,
					memberUuid,
					employerUuid
				);
				datadogRum.addAction('memberPlan: success', {
					memberUuid,
					employerUuid,
				});
			}
		},
		onError: error => {
			dispatch({
				type: 'set_search_state_error',
				facets: {} as AlgoliaFacetsType,
				searchError: error,
			});
			datadogRum.addAction('memberPlan: error', {
				memberUuid,
				employerUuid,
				error,
			});
		},
	});

	if (searchState.facets === null || !user) {
		return <LoadingOverlay />;
	}

	const routing = {
		router: history(),
		stateMapping: {
			stateToRoute(uiState) {
				return formatStateToRoute(uiState, searchState.index);
			},
			routeToState(routeState) {
				return formatRouteToState(routeState, searchState.index);
			},
		},
	};

	const handleStateChange = ({ uiState, setUiState }) => {
		const index = searchState.index;
		return onStateChange({ uiState, setUiState, index });
	};

	return (
		<SearchDispatchContext.Provider value={{ dispatch }}>
			<SearchContext.Provider value={searchState}>
				<InstantSearch
					indexName={searchState.index}
					onStateChange={handleStateChange}
					routing={routing}
					searchClient={algoliaClient}
				>
					<Configure
						clickAnalytics={true}
						filters={searchState.filters}
						getRankingInfo={true}
						hitsPerPage={HITS_PER_PAGE}
						userToken={user.id}
					/>
					{children}
				</InstantSearch>
			</SearchContext.Provider>
		</SearchDispatchContext.Provider>
	);
};
